import {
  Container,
  Rectangle,
  RenderTexture,
  Sprite,
  Texture,
  type Filter,
  type Renderer,
  type Ticker,
} from 'pixi.js';
import { keepAlive } from '../textureLifetime';
import {
  decayFlash,
  moodFor,
  moodWithFlash,
  NEUTRAL_MOOD,
  withDyingSpeed,
  type Mood,
} from './mood';
import type { DisturbanceKind } from './disturbance';
import { ScalarField } from './field';
import {
  HalftoneFilter,
  ScalarFieldFilter,
  type HalftoneUniformValues,
  type ScalarFieldUniformValues,
} from './filters';
import { AsciiArt } from './ascii';
import {
  FIELD_RENDER_SCALE,
  asciiCellSize,
  asciiGrid,
  halftoneBinaryOf,
  normalizeRenderScale,
  pixelExponentOf,
  halftoneGridScale,
  renderMode,
  type RenderMode,
} from './renderScale';

/**
 * **游戏背景** —— 把 CPU 控制器、两个着色器阶段和一个全屏占位精灵装起来。
 *
 * ```
 *   ScalarField（CPU：扔扰动、回收、打包）
 *        │  每帧写 uniform
 *        ▼
 *   Sprite(Texture.WHITE)  ──filters──▶  [ScalarFieldFilter, HalftoneFilter]
 *        │  铺满逻辑画布
 *        ▼
 *   背景
 * ```
 *
 * ## 为什么下面垫一张白色精灵
 *
 * Pixi 的 `Filter` 是"把上游画的东西加工一遍"。阶段一其实不需要上游 ——
 * 场是从参数现算的 —— 但过滤器管线总得有个输入。垫一张铺满的白色精灵
 * 是最省事、行为最可预测的做法（阶段一会忽略它）。
 *
 * ## 时间轴
 *
 * `uTime` 自己累加 `ticker.deltaMS`，**不用 `Date.now()`**：
 * - 可以暂停（把 `timeScale` 设 0）
 * - 可以倒放/跳转（将来做回放时有用）
 * - 和 `bornAt` 是同一条轴，不会因为系统时间跳变而错位
 */
/**
 * **标量场的基准时间倍率。**
 *
 * ⚠️ **这个数是评审自己调的**（原话：「时间倍率和 Bar 参数是我修改的」）。
 * 曾经按"播放速度变成十分之一"设成 0.1，后来他自己改回了 1 ——
 * 别再照着自己的理解往回改。
 *
 * 它乘在**场自己的时间轴**上（`#time`）：底噪漂移、环境扰动、
 * 以及一切走 `#time` 的东西。
 *
 * ⚠️ 实际用的倍率还会再乘一道 energy 的影响：`base × (1 + energy × 0.05)`
 * （评审要求，见 `mood.ts` 的 `timeScaleFor`）—— 所以这里只是**基准**。
 *
 * ⚠️ **事件驱动的扰动要自己补回来**：Bar 那圈脉冲的 `speed` / `life` 是按
 * **墙上时间**定的，而 `#time` 可能慢（或快）十倍。`spawnBarPulse` 的
 * `fieldRate` 参数就是干这个的，`Background.pulseFromCenter` 会把
 * `#timeScale` 传进去。
 */
export const FIELD_TIME_SCALE = 1;

/**
 * 世界变换 + 屏幕尺寸。
 *
 * 画布逻辑坐标 → 屏幕：`screen = world + logical * scale`
 * （`render/app.ts` 每帧按"等比缩放 + 居中"设好，`viewportOf` 就是它的逆）
 */
export interface Viewport {
  readonly scale: number;
  readonly x: number;
  readonly y: number;
  readonly screenWidth: number;
  readonly screenHeight: number;
}

/**
 * 从世界容器当前的变换算出视口。**唯一**一处把这些数凑起来的地方。
 *
 * ⚠️ 参数写成结构类型而不是 `Container`：这样不 import Pixi 的那个类也能用，
 * 而且"我只是读一下缩放和平移"这件事在签名上就说清楚了。
 */
export function viewportOf(
  world: { readonly scale: { readonly x: number }; readonly position: { readonly x: number; readonly y: number } },
  screen: { readonly width: number; readonly height: number },
): Viewport {
  return {
    scale: world.scale.x,
    x: world.position.x,
    y: world.position.y,
    screenWidth: screen.width,
    screenHeight: screen.height,
  };
}

/** 视口 → 四边形（画布逻辑坐标）。场就是铺在这一块上的 */
export function quadOf(viewport: Viewport): readonly [number, number, number, number] {
  const scale = viewport.scale > 0 ? viewport.scale : 1;
  return [
    noNegativeZero(-viewport.x / scale),
    noNegativeZero(-viewport.y / scale),
    viewport.screenWidth / scale,
    viewport.screenHeight / scale,
  ];
}

/** `-0` 画出来和 `0` 一模一样，但没必要让它流进 uniform（断言也不认它） */
function noNegativeZero(v: number): number {
  return v === 0 ? 0 : v;
}

export interface BackgroundOptions {
  /** 逻辑画布尺寸 */
  readonly width: number;
  readonly height: number;
  /**
   * 渲染器。**降分辨率那一层要用它**：场先画进一张小 FBO，再放大到屏幕上。
   *
   * ⚠️ 不给就是满分辨率（`pixelExponent = 0`）—— 单测和"只是想看一眼"的场合
   * 不必为此准备一个 Pixi 应用（调试页都给了）。
   */
  readonly renderer?: Renderer;
  /**
   * **渲染档位**（见 `renderScale.ts`）。不给就用 `FIELD_RENDER_SCALE`。
   *
   * ⚠️ 游戏里它**不是**热更参数（硬编码，界面上没有入口）。构造函数留这个
   * 口子一是让测试能钉住三档各自的行为，二是给调试页做
   * "`?scale=n` 看一眼每一档长什么样"（只有调试页，游戏不读 URL）。
   */
  readonly renderScale?: number;
  /**
   * 卡牌占地遮罩。**必填** —— 标量场要在被卡牌盖住的地方置零
   * （评审要求：在标量场和卡牌层都算完之后挖洞）
   */
  readonly mask: Texture;
  /** 时间倍率。0 = 冻住 */
  readonly timeScale?: number;
  /** 阶段一的参数。不给就用默认（见 `ScalarFieldFilter` 里的初值） */
  readonly field?: Partial<ScalarFieldUniformValues>;
  /** 阶段二的参数（网点节距、纸色墨色……） */
  readonly halftone?: Partial<HalftoneUniformValues>;
}

export class Background {
  /**
   * 加到场景里的东西。
   *
   * ⚠️ 它从一个 Sprite 变成了**容器**：三种档位换的是"把哪一层摆出去"——
   *    原样档直接摆里层（过滤器就地合成到屏幕上，和像素化之前**一模一样**），
   *    另外两档摆"那张 FBO 铺满视口"的精灵。
   */
  readonly view: Container;

  readonly field: ScalarField;
  readonly fieldFilter: ScalarFieldFilter;
  readonly halftoneFilter: HalftoneFilter;
  /** ASCII 那一档的画笔。**第一次真的用到才建**（要建一个 Text 并量字形） */
  #asciiArt: AsciiArt | null = null;

  #time = 0;
  /** 基准时间倍率（`FIELD_TIME_SCALE`）。实际用的是它乘上 energy 的影响 */
  #baseTimeScale: number;
  /** 当前实际时间倍率。读数与调试用 */
  #timeScale: number;
  /** 现在在不在濒死（只影响播放速度，见 `setDying`） */
  #dying = false;
  /** 静态情绪（energy 决定）。`null` = 还没收到 energy，按中性算 */
  #mood: Mood | null = null;
  /** Bar 那一下 0..1，每帧衰减。见 `barFlash()` */
  #flash = 0;

  /** 四边形在画布逻辑坐标里的矩形。窗口一变就变，见 `setViewport` */
  #quad: readonly [number, number, number, number] | null = null;

  /**
   * **里层那一块**：真正算场（和半调 / ASCII）的精灵。
   *
   * | 档位 | 它有多大 |
   * | --- | --- |
   * | 原样 | 视口那么大（**它自己就是画出去的那一层**） |
   * | 像素化 | 小 FBO 那么大（`1 / 2^n` 屏幕） |
   * | ASCII | `列 × 行` —— **一个字符一个纹素** |
   */
  readonly #inner: Sprite;
  /** 把小 FBO 铺满视口的那一张（原样档不用它） */
  readonly #out: Sprite;
  /** 里层的渲染目标。原样档是 `null`（不过 FBO，恢复原本那条路） */
  #rt: RenderTexture | null = null;
  readonly #renderer: Renderer | null;
  readonly #renderScale: number;
  /** 视口在画布逻辑坐标里有多大。换档位时要重摆 */
  #quadSize: readonly [number, number] = [1280, 800];

  constructor(options: BackgroundOptions) {
    this.#baseTimeScale = options.timeScale ?? FIELD_TIME_SCALE;
    this.#timeScale = this.#baseTimeScale;

    this.field = new ScalarField();

    this.fieldFilter = new ScalarFieldFilter(options.mask, {
      ...options.field,
      resolution: [options.width, options.height],
    });
    this.halftoneFilter = new HalftoneFilter({
      ...options.halftone,
      resolution: [options.width, options.height],
    });

    this.#renderer = options.renderer ?? null;
    this.#renderScale = normalizeRenderScale(options.renderScale ?? FIELD_RENDER_SCALE);
    this.#quadSize = [options.width, options.height];

    // 里层：白精灵 + 过滤器。它只负责"给过滤器一个输入"，
    // 尺寸由 `setViewport` 按档位摆（默认就是画布大小）
    this.#inner = new Sprite(Texture.WHITE);
    this.#inner.label = 'bg-field-inner';
    this.#applyStageFilters();
    this.#inner.width = options.width;
    this.#inner.height = options.height;
    // 尺寸已知，别让 Pixi 每帧递归量一遍包围盒
    this.#inner.filterArea = new Rectangle(0, 0, options.width, options.height);

    /** 外层：把 FBO 铺满整块视口的那一张。`texture` 在 `setViewport` 里换成 `#rt` */
    this.#out = new Sprite(Texture.WHITE);
    this.#out.label = 'bg-field-out';

    this.view = new Container();
    this.view.label = 'bg-field';
    // 先按原样档挂（`setViewport` 第一帧就会按真正的档位重摆）
    this.view.addChild(this.#inner);
    this.#inner.position.set(0, 0);
  }

  /** 现在是什么档位（`renderScale.ts`）。调试与测试用 */
  get renderScale(): number {
    return this.#renderScale;
  }

  /** `plain` / `pixel` / `ascii`。调试与测试用 */
  get mode(): RenderMode {
    return renderMode(this.#renderScale);
  }

  /**
   * 里层那张图（场的输出）。原样档下**没有** FBO，所以这里给的是白占位图 ——
   * 想读"画出来是什么样"用 `probe()`。
   */
  get fieldTexture(): Texture {
    return this.#rt ?? this.#inner.texture;
  }

  /**
   * 现在那块 FBO 有多大（像素）。原样档下等于视口大小（没有降分辨率）。
   * 调试与测试用。
   */
  get fieldSize(): { readonly width: number; readonly height: number } {
    const rt = this.#rt;
    return rt === null
      ? { width: Math.round(this.#inner.width), height: Math.round(this.#inner.height) }
      : { width: rt.width, height: rt.height };
  }

  /** 半调这一级现在是不是画成 1-bit 硬边（见 `FIELD_HALFTONE_BINARY`） */
  get halftoneBinary(): boolean {
    return halftoneBinaryOf(this.#renderScale);
  }

  /**
   * **里层那张精灵现在的尺寸与缩放。** 调试与测试用。
   *
   * "背景突然变大"要先排除掉最蠢的那一类原因：**精灵自己被摆大了**。
   * `view` / `#inner` 的实测尺寸都是现成的读数，量一下就能把
   * "容器/精灵被拉伸"和"着色器算错了"分开 —— 见 `debug/dyingBg.ts`。
   */
  get innerSprite(): {
    readonly width: number;
    readonly height: number;
    readonly scaleX: number;
    readonly scaleY: number;
    readonly x: number;
    readonly y: number;
  } {
    return {
      width: this.#inner.width,
      height: this.#inner.height,
      scaleX: this.#inner.scale.x,
      scaleY: this.#inner.scale.y,
      x: this.#inner.position.x,
      y: this.#inner.position.y,
    };
  }

  /** ASCII 档位下屏幕上有多少列 / 多少行字符（不是 ASCII 档就是 0） */
  get asciiCols(): number {
    return renderMode(this.#renderScale) === 'ascii'
      ? asciiGrid(this.#renderScale, this.#quadSize[0], this.#quadSize[1]).cols
      : 0;
  }

  /** ASCII 档位下有几行字符（不是 ASCII 档就是 0） */
  get asciiRows(): number {
    return renderMode(this.#renderScale) === 'ascii'
      ? asciiGrid(this.#renderScale, this.#quadSize[0], this.#quadSize[1]).rows
      : 0;
  }

  /**
   * 里层挂着几条过滤器。调试与测试用。
   *
   * ⚠️ **不能看 `view.filters`**：`view` 是容器，两级过滤器在**里层** `#inner`
   *    上（或者 ASCII 档在 `#out` 上）。`view.filters` 是 `undefined` ——
   *    不是空数组，读 `.length` 会直接抛（踩过一次）。
   */
  get stageCount(): number {
    return this.#inner.filters.length;
  }

  /**
   * **把这一帧的背景画进一张你给的 RT**（调试与冒烟用）。
   *
   * 三种档位画出来的东西不一样，而"屏幕上到底什么样"这件事只能从像素上读。
   * 画的是 `view` 那一层 —— 也就是**最终背景**（原样档含两级过滤器的合成结果，
   * 像素化档含放大，ASCII 档含字符画）。
   */
  probe(target: RenderTexture): void {
    this.#renderer?.render({ container: this.view, target, clear: true });
  }

  get time(): number {
    return this.#time;
  }

  /** 当前的灰阶窗口（lo, hi）。调试面板要读它来显示 */
  get window(): readonly [number, number] {
    const w = this.fieldFilter.fieldUniforms.uniforms.uWindow as Float32Array;
    return [w[0] ?? 0, w[1] ?? 0];
  }

  /** 改灰阶窗口。**这是"多少地方上白点"的总闸门** */
  setWindow(lo: number, hi: number): void {
    this.fieldFilter.setValues({ window: [lo, hi] });
  }

  set timeScale(value: number) {
    this.#baseTimeScale = value;
    this.#refreshTimeScale();
  }

  /**
   * **现在在不在濒死**（评审：濒死时场的播放速度翻倍）。
   *
   * ⚠️ 背景自己不认识"濒死"这个概念（那是评分表的事），所以由宿主 /
   *    `GameBackdrop` 每帧转手 —— 和 `setEnergy` 一样，只是个输入。
   */
  setDying(dying: boolean): void {
    if (dying === this.#dying) return;
    this.#dying = dying;
    this.#refreshTimeScale();
  }

  get dying(): boolean {
    return this.#dying;
  }

  /** 按"基准 × energy × 濒死翻倍"重算一次。三个入口都走它 */
  #refreshTimeScale(): void {
    this.#timeScale =
      this.#energy === null
        ? this.#baseTimeScale
        : withDyingSpeed(this.#baseTimeScale, this.#energy, this.#dying);
  }

  /** 现在的时间倍率（已经算进 energy 的影响）。调试页"暂停时间"用它恢复 */
  get timeScale(): number {
    return this.#timeScale;
  }

  /** 最近一次收到的 energy。要么是 `setEnergy` 给的，要么是调试覆写的 */
  #energy: number | null = null;

  /**
   * **按 energy 调情绪**（评审：越高越亮越橙、越低越暗越蓝）。
   *
   * ⚠️ 它同时改**时间倍率**：`base × (1 + energy × 0.05)` ——
   * energy 高时背景动得更快（更躁），低时更慢（更沉）。
   * 两件事挤在一个入口里，是因为它们都只由 energy 决定；
   * 拆成两个 setter 迟早会出现"一个更新了另一个忘了"。
   */
  setEnergy(energy: number): void {
    this.#energy = energy;
    this.#mood = moodFor(energy);
    this.#refreshTimeScale();
  }

  /**
   * Bar 到了：亮一下 + 泛红，半秒内消退。见 `mood.ts`。
   *
   * ⚠️ 调用方（`GameBackdrop.barTick`）**只在濒死期间**走到这里 ——
   * 评审：泛红是 Bar 扰动附带的，两者一起只在濒死时触发。
   */
  barFlash(): void {
    this.#flash = 1;
  }

  /** 现在这一帧的情绪（含 Bar 那一闪）。调试与冒烟用 */
  get mood(): Mood {
    return moodWithFlash(this.#mood ?? NEUTRAL_MOOD, this.#flash);
  }

  /** Bar 那一闪的当前值 0..1 */
  get flash(): number {
    return this.#flash;
  }

  /**
   * **把场铺满整块视口。**
   *
   * 参数是四边形在**画布逻辑坐标**里的矩形：`(0, 0, 1280, 800)` 就是画布本身，
   * 窗口不是 16:10 时它会比画布大（x/y 是负数）—— 于是黑边上也有场，
   * 不会出现"左边和上边空一块"。
   *
   * ⚠️ 为什么必须**显式**告诉它（而不是让 Pixi 自己算）：滤镜的四边形是
   * 纹理池按它分配的那张贴图定的，可能比申请的区域大 —— 详细的原委写在
   * `shaders.ts` 的文件头。这里传的就是"四边形到底有多大"。
   *
   * ⚠️ `filterArea` 用**局部坐标**：精灵自己挪到 `(x, y)`、局部矩形还是
   * `(0, 0, w, h)`。写 `(x, y, w, h)` 会被世界变换再加一次偏移。
   */
  setViewport(viewport: Viewport): void {
    const raw = quadOf(viewport);
    const x = raw[0];
    const y = raw[1];
    const w = Math.max(1, raw[2]);
    const h = Math.max(1, raw[3]);
    if (
      this.#quad !== null &&
      this.#quad[0] === x &&
      this.#quad[1] === y &&
      this.#quad[2] === w &&
      this.#quad[3] === h
    ) {
      return; // 每帧都调，没变就别写 uniform
    }
    this.#quad = [x, y, w, h];
    this.#quadSize = [w, h];
    const mode = renderMode(this.#renderScale);

    /**
     * ⚠️ **着色器的坐标永远是"画布逻辑坐标"那一套**（`quad = (x, y, w, h)`），
     *    和 FBO 多大**无关**：`vQuadCoord` 铺满被渲染的那一块区域，所以
     *    对着小 FBO 渲染时它照样描述屏幕上那一整块视口 —— 网点、字符格、
     *    卡牌遮罩、裂缝的位置一个都不用改。变的只有"每个像素有多大"。
     */
    const asciiCell = asciiCellSize(this.#renderScale);
    const px = 2 ** pixelExponentOf(this.#renderScale);
    const rtW = mode === 'ascii'
      ? Math.max(1, Math.ceil(w / asciiCell))
      : mode === 'pixel'
        ? Math.max(1, Math.round(w / px))
        : Math.max(1, Math.round(w));
    const rtH = mode === 'ascii'
      ? Math.max(1, Math.ceil(h / asciiCell))
      : mode === 'pixel'
        ? Math.max(1, Math.round(h / px))
        : Math.max(1, Math.round(h));

    const sizeChanged =
      this.#rt === null || this.#rt.width !== rtW || this.#rt.height !== rtH;

    /**
     * ★ **原样档（`renderScale === 0`）不走 FBO。**
     *
     * 评审：「`0` 时恢复原本的渲染，取消掉例如临近采样之类的调整。」
     * 所以这一档**里层自己就是画出去的那一层**：整屏大小的白精灵 + 两级过滤器，
     * 滤镜就地合成到屏幕上 —— 和加降分辨率之前一字不差（少一次整屏搬运，
     * 也少一次邻近采样）。
     *
     * ⚠️ 没有 renderer（单测那种场合）也走这一条：那时根本没得选。
     */
    if (mode === 'plain' || this.#renderer === null) {
      this.#rt?.destroy(true);
      this.#rt = null;
      if (!this.view.children.includes(this.#inner)) {
        this.#out.removeFromParent();
        this.view.addChild(this.#inner);
      }
      this.#applyStageFilters();
      this.#inner.position.set(x, y);
      this.#inner.width = w;
      this.#inner.height = h;
      this.#inner.filterArea = new Rectangle(0, 0, w, h);
      this.fieldFilter.setValues({ quad: [x, y, w, h] });
      // 满分辨率：表面就是这一块，一个逻辑单位 = 一个像素（网格也不放大）
      this.halftoneFilter.enabled = true;
      this.halftoneFilter.setValues({
        quad: [x, y, w, h],
        targetSize: [w, h],
        gridScale: 1,
        // ★ 0 档是不是"1-bit 硬边"由 `FIELD_HALFTONE_BINARY` 决定：
        //   关掉（默认）= 加像素化之前那条路（柔边、不吸像素格、亮度乘颜色）
        binary: halftoneBinaryOf(this.#renderScale) ? 1 : 0,
      });
      return;
    }

    /**
     * 另外两档都要一张 FBO：
     *
     * | 档位 | FBO 有多大 | 采样 |
     * | --- | --- | --- |
     * | 像素化 | `1 / 2^n` 视口 | **邻近**（复古像素风就是这一步给的） |
     * | ASCII | `列 × 行`（一个字符一个纹素） | **邻近**（每格只读一个纹素） |
     */
    if (sizeChanged) {
      this.#rt?.destroy(true);
      this.#rt = keepAlive(
        RenderTexture.create({
          width: rtW,
          height: rtH,
          resolution: 1,
          scaleMode: 'nearest',
          label: mode === 'ascii' ? 'bg-ascii-field-rt' : 'bg-field-rt',
        }),
      );
    }
    const rt = this.#rt;
    if (rt === null) return;

    // 里层只有 FBO 那么大、摆在原点；外层铺满整块视口（把 FBO 拉大）
    this.#inner.position.set(0, 0);
    this.#inner.width = rtW;
    this.#inner.height = rtH;
    this.#inner.filterArea = new Rectangle(0, 0, rtW, rtH);
    this.#out.texture = rt;
    this.#out.position.set(x, y);
    this.#out.width = w;
    this.#out.height = h;
    this.#out.filterArea = new Rectangle(0, 0, w, h);
    if (!this.view.children.includes(this.#out)) {
      this.#inner.removeFromParent();
      this.view.addChild(this.#out);
    }

    this.fieldFilter.setValues({ quad: [x, y, w, h] });

    if (mode === 'ascii') {
      /**
       * ★ **ASCII：整个半调被换掉。**
       *
       * 场渲进"一字符一纹素"的小图（里层只挂场那一级）；字符画由一个
       * Pixi `Text` 画在**满分辨率**上 —— 所以它不经过像素化。
       *
       * ⚠️ `#out` 在这里只是"让 `probe()` / 调试页能读到场"的那一层，
       *    真正画出去的是画笔自己那个 `Container`。
       */
      this.#applyStageFilters();
      /**
       * ⚠️ 把"把小 FBO 铺满视口"那张摘掉：ASCII 档画出去的是**画笔那一层**，
       *    留着 `#out` 只是白画一次（它铺的是一张 54×34 的场）。
       *    `probe()` 读的就是最终背景，这一档读到的也就是字符画 ✓。
       */
      this.#out.removeFromParent();
      const art = this.#asciiArtFor();
      art.setLayout([x, y, w, h], asciiCell);
      if (!this.view.children.includes(art.view)) this.view.addChild(art.view);
      return;
    }
    // 离开 ASCII 那一档时把画笔摘掉（它自己还留着，档位换回来不用重建）
    if (this.#asciiArt !== null && this.view.children.includes(this.#asciiArt.view)) {
      this.#asciiArt.view.removeFromParent();
    }
    if (!this.view.children.includes(this.#out)) this.view.addChild(this.#out);

    // 像素化：两级都在里层，外层只是把小 FBO 拉大
    this.#applyStageFilters();
    this.#out.filters = [];
    this.halftoneFilter.enabled = true;
    /**
     * ★ **把 FBO 的像素尺寸告诉半调那一级** —— 这就是"网点和像素网格对齐"
     *   的全部内容：`quad.zw / targetSize` = 一个逻辑单位等于几个 FBO 像素，
     *   着色器拿它把采样点吸附到像素格上。
     *
     * ⚠️ 必须**显式**给：着色器没法自己知道（`uInputSize` / `uOutputTexture`
     *    都是 Pixi 纹理池的尺寸，比这块 FBO 大一圈）。见 `shaders.ts`。
     *
     * ★ **`gridScale` 是线性的 `n`**（评审：「半调网格也跟着线性放大，不和
     *   像素化一样 2^x 放大」）：n=1 是设计尺寸 11 像素，n=2 是 22 像素。
     */
    this.halftoneFilter.setValues({
      quad: [x, y, w, h],
      targetSize: [rtW, rtH],
      gridScale: halftoneGridScale(this.#renderScale),
      binary: halftoneBinaryOf(this.#renderScale) ? 1 : 0,
    });
  }

  /** ASCII 那一档的画笔（第一次用才建） */
  #asciiArtFor(): AsciiArt {
    this.#asciiArt ??= new AsciiArt();
    return this.#asciiArt;
  }

  /** 现在铺的是哪一块（画布逻辑坐标）。读数与调试用 */
  get quad(): readonly [number, number, number, number] | null {
    return this.#quad;
  }

  /**
   * 换濒死裂缝遮罩，并给一个强度（0..1）。
   *
   * 强度是**加淡出**用的：裂缝那一层自己会画在同一张 RT 上，"消失"靠这个
   * 系数而不是靠把线擦掉 —— 擦掉要每帧重画一次空图，而那和"画出来"一样贵。
   */
  setCrack(mask: Texture, strength: number): void {
    if (this.#crackMask !== mask) {
      this.#crackMask = mask;
      this.fieldFilter.setCrack(mask);
    }
    this.fieldFilter.setValues({ crackStrength: strength });
  }

  #crackMask: Texture | null = null;

  /** 在归一化坐标（0..1）扔一条扰动。鼠标点击走这里 */
  spawn(kind: DisturbanceKind, x: number, y: number): void {
    this.field.spawn(kind, x, y, this.#time);
  }

  /**
   * **从画面正中向外推一圈脉冲。** 每小节一次（Bar 事件，见 `ui/beat.ts`）。
   *
   * `strength` 是 0..1 的**系数**（乐句头 1、其余小节打折），乘在 `amp` 上。
   *
   * ⚠️ 时间用的是**这一层自己的轴**（`#time`，只被 `update` 推进）。
   * 拍子是从回调里进来的，可能落在两帧之间 —— 用 `#time` 意味着这一发最多
   * 早一帧出生（不到 17ms），肉眼看不出来；而换成 `performance.now()`
   * 就等于让画面多出一条**和暂停、回放都不兼容**的时间轴。
   *
   * 具体"一发脉冲长什么样"在 `ScalarField.spawnBarPulse` 里，见它。
   *
   * ⚠️ 传 `this.#timeScale` 进去：脉冲的 `speed` / `life` 是按**墙上时间**
   * 定的（一个 bar 扫到边角），而 `#time` 现在慢十倍，不补的话这一圈要
   * 十三秒才淌出去 —— 那就不是"跟着小节"了。
   */
  pulseFromCenter(strength = 1): void {
    this.field.spawnBarPulse(this.#time, strength, this.#timeScale);
  }

  /**
   * 每帧推进。挂到 ticker 上。
   *
   * ⚠️ 全项目只允许**一个** ticker 回调（`architecture.md` §7.1），
   * 所以这个方法是给那唯一一个回调调的，不要自己 `app.ticker.add`。
   */
  update(ticker: Ticker): void {
    const wall = ticker.deltaMS / 1000;
    // ⚠️ Bar 那一闪走**墙上时间**：它是"音乐敲了一下"的视觉回声，
    //    不该跟着 energy 改倍率（那会让高能量时红闪拖得更久）
    this.#flash = decayFlash(this.#flash, wall);

    const dt = wall * this.#timeScale;
    this.#time += dt;

    this.field.tick(dt, this.#time);

    this.fieldFilter.setValues({ time: this.#time });
    this.fieldFilter.setPack(this.field.pack(this.#time));

    const mood = this.mood;
    this.halftoneFilter.setValues({ brightness: mood.brightness, tint: mood.tint });

    /**
     * ★ **把这一帧画进那块小 FBO。**
     *
     * - 像素化档：两级过滤器都跑在这张小贴图上（Pixi 按被渲染区域的包围盒分配
     *   中间贴图，所以它们也跟着变小）；外层那张精灵再把它邻近采样放大
     * - ASCII 档：这一趟只跑**场**那一级（一字符一纹素）；字符画是外层那张精灵
     *   上的 `AsciiFilter` 在**满分辨率**下画的 —— 所以它不像素化
     * - 原样档：没有 FBO（`#rt` 是 `null`），这一趟什么都不做，两级过滤器在
     *   正常渲染时**就地**合成到屏幕上
     */
    const rt = this.#rt;
    const renderer = this.#renderer;
    if (rt !== null && renderer !== null) {
      renderer.render({ container: this.#inner, target: rt, clear: true });
      /**
       * ★ ASCII 那一档：把这张"一字符一纹素"的小图读回来 → 交给
       * `ts-ascii-engine` 换成字符 → 摆成一个 `Text`。
       */
      if (this.mode === 'ascii') {
        this.#asciiArt?.update(renderer, rt, mood.tint, ticker.deltaMS);
      }
    }
  }

  /** 调试用：单独开关某一级，判断"不对的是场还是网点" */
  setStageVisible(stage: 'field' | 'halftone', visible: boolean): void {
    if (stage === 'field') this.fieldFilter.enabled = visible;
    else this.halftoneFilter.enabled = visible;
  }

  /**
   * **调试用：把第二级（半调）整个换掉。**
   *
   * 这是"背景看起来变大了，问题在着色器里还是在外头"的判据：换上一个只输出
   * 坐标的滤镜（见 `debug/dyingBg.ts` 的 `DiagFilter`），屏幕上就只剩
   * **映射本身** —— 从画布边缘到中心的颜色梯度应当是一路匀速的。梯度如果
   * 在中途折了一下、或者只覆盖了半块屏幕，那问题就在映射里；梯度完全正常而
   * 网点还是变大，那问题在别处。
   *
   * ⚠️ 传 `null` 恢复正常的半调。**游戏里永远不传**，只有调试页用。
   */
  setDiagnostic(filter: Filter | null): void {
    this.#diagnostic = filter;
    this.#applyStageFilters();
  }

  #diagnostic: Filter | null = null;

  /**
   * 按当前档位（和有没有诊断滤镜）把里层挂的那两级接好。
   *
   * ⚠️ 只有这一处决定 `#inner.filters` 是什么 —— 散在四个分支里写过一次，
   * 加诊断滤镜时就得改四处，漏一处就变成"档位一切回来诊断滤镜就不见了"。
   */
  #applyStageFilters(): void {
    const second = this.#diagnostic ?? (this.mode === 'ascii' ? null : this.halftoneFilter);
    this.#inner.filters = second === null ? [this.fieldFilter] : [this.fieldFilter, second];
  }

  /**
   * **ASCII 那一档的字符画本身**（调试与测试用）。没建过就是 `null`。
   *
   * 读的是屏幕上那行字：`rows` 行、`cols` 列，行高正好一格。
   */
  get asciiArt(): { readonly text: string; readonly chars: number } | null {
    const art = this.#asciiArt;
    if (art === null) return null;
    const text = art.view.children.find((c) => c.label === 'bg-ascii-text');
    const value = (text as { text?: string } | undefined)?.text ?? '';
    return { text: value, chars: value.replace(/\n/g, '').length };
  }

  destroy(): void {
    // ⚠️ 里层现在是 `view` 的孩子，销毁 `view` 会连带销毁它 —— 不用单独来一遍
    this.#rt?.destroy(true);
    this.#rt = null;
    this.view.destroy({ children: true });
    this.#asciiArt?.destroy();
    this.#asciiArt = null;
    this.fieldFilter.destroy();
    this.halftoneFilter.destroy();
  }
}

/** 背景包在容器里方便整体调层 —— 目前直接返回精灵，留着这层是为了将来加东西 */
export function backgroundLayer(background: Background): Container {
  return background.view;
}
